<template>
  <b-modal v-model="localVisible" title="Validation rules" size="lg" @ok="onSave" @hide="onHide">
    <template #title>
      Validation rules
      <modal-help topic="validation-rules" />
    </template>
    <p class="vr-intro">The first matching rule decides. Block rejects the save with the message you write. Allow accepts this value and skips the rules below it. Required and unique still apply.</p>
    <div v-if="localRules.length" class="vr-list">
      <div v-for="(rule, index) in localRules" :key="rule.id" class="vr-rule">
        <button type="button" class="vr-open" @click="editRule(index)">
          <span class="vr-type">{{ typeLabel(rule.type) }}</span>
          <span class="vr-summary">{{ ruleSummary(rule) }}</span>
          <span class="vr-action" :class="'vr-action-' + rule.action">{{ rule.action === 'allow' ? 'Allow' : 'Block' }}</span>
        </button>
        <button type="button" class="vr-remove" aria-label="Remove rule" @click="removeRule(index)">&times;</button>
      </div>
    </div>
    <div v-else-if="editingIndex === null" class="vr-empty">No validation rules yet. A save is blocked only by required, unique, and the rules you add here.</div>
    <div v-if="editingIndex !== null" class="vr-edit">
      <div class="vr-edit-title">{{ editingIndex === localRules.length ? 'New rule' : 'Edit rule' }}</div>
      <label class="vr-label">Rule type</label>
      <select v-model="editType" class="vr-select">
        <option v-for="option in typeOptions" :key="option" :value="option">{{ typeLabel(option) }}</option>
      </select>
      <template v-if="editType !== 'else'">
        <div class="vr-conditions-head">
          <span>Conditions</span>
          <span v-if="editConditions.length > 1" class="vr-match">
            <button type="button" :class="{ active: editMatchMode === 'all' }" @click="editMatchMode = 'all'">All</button>
            <button type="button" :class="{ active: editMatchMode === 'any' }" @click="editMatchMode = 'any'">Any</button>
          </span>
        </div>
        <div v-for="(condition, index) in editConditions" :key="index" class="vr-condition">
          <select v-model="condition.source" class="vr-select" @change="onSourceChange(condition)">
            <option value="self">This column</option>
            <option value="field">Another column</option>
          </select>
          <select v-if="condition.source === 'field'" v-model="condition.field" class="vr-select" @change="onFieldChange(condition)">
            <option value="">Select column</option>
            <option v-for="column in otherColumns" :key="column.uniqueTitle" :value="column.uniqueTitle">{{ column.title || column.uniqueTitle }}</option>
          </select>
          <select v-model="condition.operator" class="vr-select">
            <option v-for="operator in operatorsFor(condition)" :key="operator.key" :value="operator.key">{{ operator.label }}</option>
          </select>
          <template v-if="needsValue(condition.operator)">
            <select v-model="condition.valueSource" class="vr-select">
              <option value="literal">A value</option>
              <option value="field">Another column</option>
            </select>
            <select v-if="condition.valueSource === 'field'" v-model="condition.valueField" class="vr-select">
              <option value="">Select column</option>
              <option v-for="column in otherColumns" :key="'value-' + column.uniqueTitle" :value="column.uniqueTitle">{{ column.title || column.uniqueTitle }}</option>
            </select>
            <input v-else v-model="condition.value" class="vr-input" :type="inputType(condition)" :placeholder="valuePlaceholder(condition)">
          </template>
          <button type="button" class="vr-remove" aria-label="Remove condition" @click="editConditions.splice(index, 1)">&times;</button>
        </div>
        <button type="button" class="vr-add" @click="addCondition">Add condition</button>
      </template>
      <label class="vr-label">When this rule matches</label>
      <select v-model="editAction" class="vr-select">
        <option value="block">Block the save</option>
        <option value="allow">Allow the save</option>
      </select>
      <label v-if="editAction === 'block'" class="vr-label">Message</label>
      <input v-if="editAction === 'block'" v-model="editMessage" class="vr-input" placeholder="What the person should read">
      <div class="vr-edit-actions">
        <button type="button" class="btn btn-primary btn-sm" @click="saveEdit">Save rule</button>
        <button type="button" class="btn btn-secondary btn-sm" @click="cancelEdit">Cancel</button>
      </div>
    </div>
    <button v-if="editingIndex === null" type="button" class="vr-add" @click="startRule">Add rule</button>
  </b-modal>
</template>

<script>
import { dataTypeForColumn, isBlankOperator, normalizeValidationRules, validationOperators } from '../../../../shared/column-validation.js'

let nextRuleId = 1

function blankCondition (dataType) {

  return {
    source: 'self',
    field: '',
    operator: dataType === 'number' ? '>' : (dataType === 'datetime' ? 'before' : 'matches'),
    dataType,
    valueSource: 'literal',
    valueField: '',
    value: ''
  }
}

export default {
  props: {
    visible: { type: Boolean, default: false },
    rules: { type: Array, default: () => [] },
    column: { type: Object, default: () => ({}) },
    columns: { type: Array, default: () => [] }
  },
  data () {

    return {
      localVisible: false,
      localRules: [],
      editingIndex: null,
      editType: 'if',
      editMatchMode: 'all',
      editConditions: [],
      editAction: 'block',
      editMessage: ''
    }
  },
  computed: {
    columnDataType () {

      return dataTypeForColumn(this.column)
    },
    otherColumns () {

      const own = this.column && this.column.uniqueTitle

      return (this.columns || []).filter((column) => column && column.uniqueTitle && column.uniqueTitle !== own && column.type !== 'formula')
    },
    typeOptions () {

      if (!this.localRules.length || this.editingIndex === 0) return ['if']
      if (this.localRules.some((rule, index) => rule.type === 'else' && index !== this.editingIndex)) return ['elseIf']

      return ['elseIf', 'else']
    }
  },
  watch: {
    visible: {
      immediate: true,
      handler (value) {

        this.localVisible = value
        if (!value) return

        this.localRules = normalizeValidationRules({ rules: this.rules }).rules.map((rule) => Object.assign({}, rule, {
          conditions: (rule.conditions || []).map((condition) => Object.assign({}, condition))
        }))
        this.cancelEdit()
      }
    }
  },
  methods: {
    typeLabel (type) {

      if (type === 'elseIf') return 'Else if'
      if (type === 'else') return 'Else'

      return 'If'
    },
    ruleSummary (rule) {

      if (rule.type === 'else') return 'Anything that did not match above'

      const parts = (rule.conditions || []).map((condition) => this.conditionSummary(condition))

      return parts.length ? parts.join(rule.conditionMatchMode === 'any' ? ' or ' : ' and ') : 'Always'
    },
    conditionSummary (condition) {

      const operators = validationOperators(condition.dataType || this.columnDataType)
      const operator = operators.find((item) => item.key === condition.operator)
      const label = operator ? operator.label : condition.operator
      const subject = condition.source === 'field' ? (condition.field || 'another column') : 'this column'

      if (isBlankOperator(condition.operator) || condition.operator === 'taken') return `${subject} ${label}`

      const expected = condition.valueSource === 'field' ? (condition.valueField || 'another column') : (condition.value === '' || condition.value == null ? '…' : condition.value)

      return `${subject} ${label} ${expected}`
    },
    operatorsFor (condition) {

      const column = condition.source === 'field'
        ? this.otherColumns.find((item) => item.uniqueTitle === condition.field)
        : this.column

      return validationOperators(dataTypeForColumn(column || this.column))
    },
    needsValue (operator) {

      return !isBlankOperator(operator) && operator !== 'taken'
    },
    inputType (condition) {

      return (condition.dataType || this.columnDataType) === 'number' ? 'number' : 'text'
    },
    valuePlaceholder (condition) {

      if (condition.operator === 'matches' || condition.operator === 'notMatches') return 'pattern'

      return 'value'
    },
    onSourceChange (condition) {

      condition.field = ''
      condition.dataType = condition.source === 'self' ? this.columnDataType : ''
      condition.operator = this.operatorsFor(condition)[0].key
    },
    onFieldChange (condition) {

      const column = this.otherColumns.find((item) => item.uniqueTitle === condition.field)

      condition.dataType = dataTypeForColumn(column)
      condition.operator = validationOperators(condition.dataType)[0].key
    },
    addCondition () {

      this.editConditions.push(blankCondition(this.columnDataType))
    },
    startRule () {

      this.editingIndex = this.localRules.length
      this.editType = this.typeOptions[0]
      this.editMatchMode = 'all'
      this.editConditions = this.editType === 'else' ? [] : [blankCondition(this.columnDataType)]
      this.editAction = 'block'
      this.editMessage = ''
    },
    editRule (index) {

      const rule = this.localRules[index]

      this.editingIndex = index
      this.editType = rule.type
      this.editMatchMode = rule.conditionMatchMode || 'all'
      this.editConditions = (rule.conditions || []).map((condition) => Object.assign({}, condition))
      this.editAction = rule.action === 'allow' ? 'allow' : 'block'
      this.editMessage = rule.message || ''
    },
    cancelEdit () {

      this.editingIndex = null
      this.editConditions = []
    },
    saveEdit () {

      const rule = {
        id: this.editingIndex < this.localRules.length && this.localRules[this.editingIndex]
          ? this.localRules[this.editingIndex].id
          : `rule_${Date.now()}_${nextRuleId++}`,
        type: this.editType,
        conditionMatchMode: this.editMatchMode,
        conditions: this.editType === 'else' ? [] : this.editConditions.map((condition) => Object.assign({}, condition, {
          dataType: condition.dataType || this.columnDataType
        })),
        action: this.editAction,
        message: this.editAction === 'block' ? this.editMessage.trim() : ''
      }

      if (this.editingIndex >= this.localRules.length) this.localRules.push(rule)
      else this.localRules.splice(this.editingIndex, 1, rule)

      this.cancelEdit()
    },
    removeRule (index) {

      this.localRules.splice(index, 1)
      if (this.editingIndex === index) this.cancelEdit()
    },
    onSave () {

      if (this.editingIndex !== null) this.saveEdit()

      this.$emit('save', normalizeValidationRules({ rules: this.localRules }).rules)
    },
    onHide () {

      this.$emit('update:visible', false)
    }
  }
}
</script>

<style scoped>
.vr-intro, .vr-empty {
  color: #8a8a88;
  font-size: 0.85rem;
}
.vr-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 12px 0;
}
.vr-rule, .vr-condition, .vr-edit-actions, .vr-conditions-head, .vr-match {
  display: flex;
  align-items: center;
  gap: 8px;
}
.vr-open {
  flex: 1;
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
  background: #1c1c1a;
  color: #e8e8e6;
  border: 1px solid #3a3a37;
  border-radius: 6px;
  padding: 8px 10px;
}
.vr-type, .vr-action {
  font-size: 0.72rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.vr-summary {
  flex: 1;
}
.vr-action-block {
  color: #ff8b8b;
}
.vr-action-allow {
  color: #9ad12b;
}
.vr-remove {
  border: 0;
  background: transparent;
  color: #f82471;
  font-size: 1.1rem;
}
.vr-edit {
  border: 1px solid #3a3a37;
  border-radius: 6px;
  padding: 12px;
  margin-top: 12px;
}
.vr-edit-title, .vr-label {
  display: block;
  margin: 8px 0 4px;
  color: #b0b0ae;
  font-size: 0.85rem;
}
.vr-select, .vr-input {
  background: #141412;
  color: #e8e8e6;
  border: 1px solid #3a3a37;
  border-radius: 4px;
  padding: 4px 8px;
}
.vr-condition {
  margin-top: 8px;
  flex-wrap: wrap;
}
.vr-add {
  margin-top: 8px;
  border: 0;
  background: transparent;
  color: #6cb6ff;
  padding: 0;
}
.vr-match button {
  border: 1px solid #3a3a37;
  background: transparent;
  color: #b0b0ae;
  border-radius: 4px;
  padding: 2px 8px;
}
.vr-match button.active {
  color: #e8e8e6;
  border-color: #6cb6ff;
}
</style>
